import { Box, Stack } from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { parseAsStringEnum, useQueryState } from "nuqs";
import { useMemo } from "react";

import { useBillingTransactionsQuery } from "Billing/Api/Queries/useBillingTransactionsQuery";
import { localizeDescription } from "Billing/Utils/localizeDescription";
import { useTableSearchParams } from "Forms/Hooks/useTableSearchParams";
import { getTransactionTypeOptions } from "Forms/Utils/EnumOptions";
import {
  EntryDirection,
  type TransactionListItemDto,
  TransactionType,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { InvoiceDownloadIconButton } from "Invoices/Components/InvoiceDownloadIconButton";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import { BlDropdown, type BlDropdownValueType } from "Shared/Inputs/BlDropdown";
import { Price } from "Shared/Price/Price";
import { DateFormat, formatDate } from "Shared/Utils/Format/date";

const columnHelper = createColumnHelper<TransactionListItemDto>();

export const TransactionHistoryTable = (): React.ReactNode => {
  const { t } = useResource();

  const [transactionTypeFilter, setTransactionTypeFilter] = useQueryState(
    "type",
    parseAsStringEnum<TransactionType>(Object.values(TransactionType)),
  );

  const { pageIndex, pageSize, onPaginationChange, resetPage } =
    useTableSearchParams();

  const handleFilterChange = (value: BlDropdownValueType) => {
    setTransactionTypeFilter(value === "" ? null : (value as TransactionType));
    resetPage();
  };

  const { data: transactionsData, isLoading } = useBillingTransactionsQuery({
    offset: pageIndex * pageSize,
    limit: pageSize,
    transactionType: transactionTypeFilter,
  });

  const transactions = transactionsData?.items ?? [];
  const totalCount = transactionsData?.totalCount ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor("effectiveAt", {
        header: t(Resources.Billing.Date),
        enableSorting: false,
        cell: (info) => (
          <Box>{formatDate(info.getValue(), DateFormat.DateTime)}</Box>
        ),
      }),
      columnHelper.accessor("transactionType", {
        header: t(Resources.Billing.Type),
        enableSorting: false,
        cell: (info) => {
          const type = info.getValue();
          const typeKey =
            type as keyof typeof Resources.Billing.TransactionTypes;
          const proxy = Resources.Billing.TransactionTypes[typeKey];
          const translated = proxy ? t(proxy) : type;
          return <Box>{translated}</Box>;
        },
      }),
      columnHelper.accessor("description", {
        header: t(Resources.Billing.Description),
        enableSorting: false,
        meta: { fillRow: true },
        cell: (info) => (
          <Box>{localizeDescription(info.getValue()) || "-"}</Box>
        ),
      }),
      columnHelper.accessor("amount", {
        header: t(Resources.Billing.Amount),
        enableSorting: false,
        cell: (info) => {
          const direction = info.row.original.direction;
          const isCredit = direction === EntryDirection.Credit;
          const prefix = isCredit ? "+" : "-";
          return (
            <Box
              sx={(theme) => ({
                color: isCredit
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                fontWeight: 600,
              })}
            >
              {prefix}
              <Price amount={Math.abs(info.getValue())} />
            </Box>
          );
        },
      }),
      columnHelper.accessor("balanceAfter", {
        header: t(Resources.Billing.BalanceAfter),
        enableSorting: false,
        cell: (info) => (
          <Box>
            <Price amount={info.getValue()} />
          </Box>
        ),
      }),
      columnHelper.display({
        id: "invoice",
        header: "",
        cell: (info) => {
          const invoiceID = info.row.original.invoiceID;
          if (!invoiceID) return null;
          return <InvoiceDownloadIconButton invoiceID={invoiceID} />;
        },
      }),
    ],
    [t],
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getRowId: (row) => row.walletTransactionID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: totalCount,
    state: { pagination: { pageIndex, pageSize } },
    onPaginationChange,
  });

  return (
    <Box>
      <Stack direction="row" spacing={Spacing.M} sx={{ mb: Spacing.M }}>
        <BlDropdown
          name="transactionTypeFilter"
          options={getTransactionTypeOptions()}
          value={transactionTypeFilter ?? ""}
          onChange={handleFilterChange}
          showEmptyOptionLabel
          size="small"
          sx={{ minWidth: 200 }}
        />
      </Stack>
      <BlTanstackTable
        table={table}
        isLoading={isLoading}
        totalCount={totalCount}
      />
    </Box>
  );
};
